/**
 * DocSmartButton — botón de las fichas que dice cuánta documentación hay
 * vinculada a un elemento y abre la ventana para gestionarla.
 *
 * QUÉ HABÍA ANTES
 * ---------------------------------------------------------------------------
 * El modal tenía los colores escritos a mano en cada línea: títulos en
 * `#1e293b`, textos en `#64748b`/`#94a3b8`, bordes `#f1f5f9`/`#e2e8f0`, la banda
 * de acciones en `#fafbfc` (casi blanca) y los estados en pastel claro. En los
 * skins oscuros eso dejaba media ventana lavada, con textos ilegibles y una
 * franja blanca al pie. Además: emojis como iconos (📝 📎 🔗), `window.confirm`
 * para desvincular, `alert()` para los errores y los cuatro botones de abajo en
 * una rejilla fija que se comía el sitio de la lista cuando había muchos
 * documentos.
 *
 * AHORA
 * ---------------------------------------------------------------------------
 * Todo con los tokens del sistema y las piezas de la casa:
 *  · el modal es el `Dialog` común (superficie opaca, Esc, clic fuera);
 *  · los iconos son de Heroicons según el tipo (texto, fichero, enlace);
 *  · el estado es un `list-badge` (Publicado / Borrador) y las líneas usan
 *    `list-hint` / `list-code`;
 *  · la lista **tiene su propio scroll y un mínimo de alto**, así que con muchos
 *    documentos sigue viéndose bien y las acciones no la aplastan (los botones
 *    van al pie del diálogo, que es donde el usuario los busca);
 *  · desvincular pregunta con el diálogo común y los errores se enseñan en la
 *    propia ventana, no en un `alert`.
 *
 * Un documento puede estar vinculado a VARIOS elementos (relación N:M).
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DocumentTextIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  ArrowTopRightOnSquareIcon,
  ArrowDownTrayIcon,
  LinkIcon,
  PaperClipIcon,
  GlobeAltIcon,
  DocumentPlusIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import Dialog from './common/Dialog';
import { autorLegible, fechaCorta } from '../utils/autores';

/** Icono del tipo de documento (antes eran emojis). */
function IconoTipo({ tipo, style }) {
  if (tipo === 'file') return <PaperClipIcon style={style} />;
  if (tipo === 'url') return <LinkIcon style={style} />;
  return <DocumentTextIcon style={style} />;
}

export default function DocSmartButton({ elementType, elementId, elementName, companyId, variant = 'smart' }) {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [mode, setMode] = useState('list'); // 'list' | 'search' | 'url'
  const [uploading, setUploading] = useState(false);

  const [search, setSearch] = useState('');
  const [searchRes, setSearchRes] = useState([]);
  const [searching, setSearching] = useState(false);

  const [urlTitle, setUrlTitle] = useState('');
  const [urlHref, setUrlHref] = useState('');

  // Qué documento está pendiente de confirmar para desvincular.
  const [aDesvincular, setADesvincular] = useState(null);

  const resetSubViews = () => {
    setMode('list');
    setSearch(''); setSearchRes([]);
    setUrlTitle(''); setUrlHref('');
    setError('');
  };

  const loadDocs = useCallback(async () => {
    if (!elementType || !elementId || !companyId) return;
    try {
      const res = await api.getDocPagesForElement(elementType, elementId, companyId);
      setCount(res.count ?? 0);
      setDocs(res.data ?? []);
    } catch { /* el botón no debe romper la ficha */ }
  }, [elementType, elementId, companyId]);

  useEffect(() => { loadDocs(); }, [loadDocs]);

  const handleOpen = async () => {
    setOpen(true);
    resetSubViews();
    setLoading(true);
    try {
      const res = await api.getDocPagesForElement(elementType, elementId, companyId);
      setDocs(res.data ?? []);
      setCount(res.count ?? 0);
    } catch (err) {
      setError(err.message || 'No se pudieron cargar los documentos');
    } finally {
      setLoading(false);
    }
  };

  const doSearch = useCallback(async (q) => {
    if (!q || q.length < 2) { setSearchRes([]); return; }
    setSearching(true);
    try {
      const list = await api.getDocPages(companyId, { q });
      setSearchRes(list.filter((d) => !docs.some((x) => x.id === d.id)));
    } catch (err) {
      setError(err.message || 'No se pudo buscar');
    } finally {
      setSearching(false);
    }
  }, [companyId, docs]);

  useEffect(() => {
    if (mode !== 'search') return undefined;
    const t = setTimeout(() => doSearch(search), 300);
    return () => clearTimeout(t);
  }, [search, doSearch, mode]);

  const linkDoc = async (doc) => {
    setError('');
    try {
      await api.addDocPageLink(doc.id, companyId, elementType, elementId, elementName || null);
      setDocs((prev) => [...prev, doc]);
      setCount((prev) => prev + 1);
      setSearch(''); setSearchRes([]); setMode('list');
    } catch (err) {
      setError(err.message || 'No se pudo vincular el documento');
    }
  };

  const unlinkDoc = async (doc) => {
    setError('');
    try {
      const links = await api.getDocPageLinks(doc.id, companyId);
      const lnk = links.find((l) => l.element_type === elementType && l.element_id === elementId);
      if (lnk) await api.removeDocPageLink(doc.id, lnk.id, companyId);
      setDocs((prev) => prev.filter((d) => d.id !== doc.id));
      setCount((prev) => Math.max(0, prev - 1));
      setADesvincular(null);
    } catch (err) {
      setError(err.message || 'No se pudo desvincular');
      setADesvincular(null);
    }
  };

  const openDoc = (doc, opciones) => {
    setOpen(false);
    if (doc.doc_type === 'url' && doc.external_url) {
      window.open(doc.external_url, '_blank');
    } else if (doc.doc_type === 'file') {
      // Descarga autenticada: el navegador no puede poner la cabecera solo,
      // así que window.open() aquí acababa en «Token missing».
      api.downloadAuthedFile(
        `/api/docs/${doc.id}/download?company_id=${companyId}`,
        doc.original_name || doc.title || 'documento',
      ).catch((err) => setError(err.message || 'No se pudo descargar el documento'));
    } else {
      const params = new URLSearchParams({
        linkType: elementType,
        linkId: String(elementId),
        returnPath: window.location.pathname,
      });
      if (elementName) params.set('linkName', elementName);
      // El historial vive dentro del documento; con `tab=versions` abre ya en
      // esa pestaña para el que venga del botón del reloj.
      if (opciones?.tabVersiones) params.set('tab', 'versions');
      navigate(`/documentation/${doc.id}?${params}`);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const created = await api.uploadDocFile(companyId, file);
      await api.addDocPageLink(created.id, companyId, elementType, elementId, elementName || null);
      setDocs((prev) => [...prev, created]);
      setCount((prev) => prev + 1);
    } catch (err) {
      setError(err.message || 'No se pudo subir el archivo');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleCreateUrl = async () => {
    if (!urlTitle.trim() || !urlHref.trim()) {
      setError('El enlace necesita título y URL');
      return;
    }
    setError('');
    try {
      const created = await api.createDocPage(companyId, {
        title: urlTitle.trim(),
        doc_type: 'url',
        external_url: urlHref.trim(),
        status: 'published',
      });
      await api.addDocPageLink(created.id, companyId, elementType, elementId, elementName || null);
      setDocs((prev) => [...prev, created]);
      setCount((prev) => prev + 1);
      setUrlTitle(''); setUrlHref(''); setMode('list');
    } catch (err) {
      setError(err.message || 'No se pudo crear el enlace');
    }
  };

  const handleNewRichtext = () => {
    setOpen(false);
    const params = new URLSearchParams({
      linkType: elementType,
      linkId: String(elementId),
      returnPath: window.location.pathname,
    });
    if (elementName) params.set('linkName', elementName);
    navigate(`/documentation/new?${params}`);
  };

  // Un elemento que todavía no existe (un alta) no puede tener documentos
  // vinculados: el botón no se pinta, en vez de abrir una ventana que no puede
  // hacer nada y soltar un 400 al pulsar.
  if (!elementType || !elementId || !companyId) return null;

  return (
    <>
      <input type="file" ref={fileInputRef} style={{ display: 'none' }} onChange={handleFileUpload} />

      {/* ── Botón de la barra de accesos rápidos ── */}
      {variant === 'chip' ? (
        <button className={`sf2-chip${count ? '' : ' sf2-chip--flat'}`} type="button" onClick={handleOpen} title="Documentos vinculados">
          <b>{count}</b><span>Docs</span>
        </button>
      ) : (
        <button className="dv-smart-btn" type="button" onClick={handleOpen} title="Documentos vinculados">
          <span className="dv-smart-btn-icon">
            <DocumentTextIcon style={{ width: 18, height: 18, color: 'var(--accent)' }} />
          </span>
          <span className="dv-smart-btn-value" style={{ color: 'var(--accent)', fontSize: '0.85rem' }}>{count}</span>
          <span className="dv-smart-btn-label">Docs</span>
        </button>
      )}

      {/* ── Ventana (diálogo común: superficie opaca, Esc y clic fuera) ── */}
      {open && (
        <Dialog
          title={`Documentos vinculados${count > 0 ? ` (${count})` : ''}`}
          icon={<DocumentTextIcon style={{ width: 18, height: 18 }} />}
          maxWidth={640}
          className="doc-modal"
          onClose={() => setOpen(false)}
          footer={(
            <>
              {mode === 'search' && (
                <button type="button" className="list-btn-secondary" onClick={() => { setMode('list'); setSearch(''); setSearchRes([]); }}>
                  Volver
                </button>
              )}
              {mode === 'url' && (
                <>
                  <button type="button" className="list-btn-secondary" onClick={() => { setMode('list'); setUrlTitle(''); setUrlHref(''); }}>
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="list-btn-primary"
                    onClick={handleCreateUrl}
                    disabled={!urlTitle.trim() || !urlHref.trim()}
                  >
                    <LinkIcon style={{ width: 14, height: 14 }} /> Crear y vincular
                  </button>
                </>
              )}
              <button type="button" className="list-btn-primary" onClick={() => setOpen(false)}>
                Cerrar
              </button>
            </>
          )}
        >
          {error && (
            <div className="list-badge list-badge--off" style={{ display: 'block', marginBottom: 10 }}>
              {error}
            </div>
          )}

          {/* Buscador de documentos existentes para vincular */}
          {mode === 'search' && (
            <div style={{ marginBottom: 10 }}>
              <label className="list-hint" style={{ display: 'block' }}>
                Buscar un documento ya creado
                <input
                  autoFocus
                  className="list-input"
                  style={{ width: '100%', marginTop: 4 }}
                  placeholder="Escribe dos letras o más…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </label>
              {searching && <p className="list-hint" style={{ margin: '6px 0 0' }}>Buscando…</p>}
              {!searching && search.length >= 2 && searchRes.length === 0 && (
                <p className="list-hint" style={{ margin: '6px 0 0' }}>Sin resultados.</p>
              )}
              {searchRes.length > 0 && (
                <div style={{ marginTop: 8, maxHeight: 200, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)' }}>
                  {searchRes.map((doc) => (
                    <button
                      key={doc.id}
                      type="button"
                      onClick={() => linkDoc(doc)}
                      style={{
                        display: 'flex', width: '100%', textAlign: 'left', alignItems: 'center', gap: 8,
                        background: 'none', border: 0, borderBottom: '1px solid var(--border)',
                        padding: '7px 10px', cursor: 'pointer', color: 'var(--text-primary)', fontSize: '0.84rem',
                      }}
                    >
                      <IconoTipo tipo={doc.doc_type} style={{ width: 15, height: 15, flexShrink: 0 }} />
                      <span style={{ fontWeight: 600 }}>{doc.title}</span>
                      {doc.description && (
                        <span className="list-hint" style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {doc.description}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Alta de enlace */}
          {mode === 'url' && (
            <div style={{ marginBottom: 10 }}>
              <label className="list-hint" style={{ display: 'block' }}>
                Título del enlace
                <input
                  className="list-input"
                  style={{ width: '100%', marginTop: 4 }}
                  value={urlTitle}
                  onChange={(e) => setUrlTitle(e.target.value)}
                  placeholder="Manual del fabricante"
                />
              </label>
              <label className="list-hint" style={{ display: 'block', marginTop: 8 }}>
                URL
                <input
                  className="list-input"
                  style={{ width: '100%', marginTop: 4 }}
                  value={urlHref}
                  onChange={(e) => setUrlHref(e.target.value)}
                  placeholder="https://…"
                />
              </label>
            </div>
          )}

          {/* Lista de documentos vinculados: con scroll propio, para que siga
              leyéndose cuando el elemento tiene muchos. */}
          <div style={{ minHeight: 140, maxHeight: 320, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)' }}>
            {loading && (
              <div className="list-loading" style={{ padding: '1rem' }}>
                <div className="spinner-border text-primary" aria-hidden="true" />
                <span>Cargando…</span>
              </div>
            )}

            {!loading && docs.length === 0 && (
              <p className="list-hint" style={{ margin: 0, padding: '1.25rem 1rem', textAlign: 'center' }}>
                Este elemento no tiene documentación vinculada. Usa los botones de abajo para vincular
                un documento que ya exista, subir un fichero o crear uno nuevo.
              </p>
            )}

            {!loading && docs.map((doc) => {
              const autor = autorLegible(doc.created_by_name || doc.created_by);
              const creado = fechaCorta(doc.created_at);
              const modificado = fechaCorta(doc.updated_at);
              // Solo se dice «modificado» si cambió el día: si no, la misma
              // fecha dos veces seguidas solo ocupa sitio.
              const seHaTocado = Boolean(modificado) && modificado !== creado;
              const tieneHistorial = doc.doc_type === 'richtext' && (doc.latest_version || 0) > 0;
              const esFichero = doc.doc_type === 'file';
              return (
                <div
                  key={doc.id}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '9px 10px', borderBottom: '1px solid var(--border)',
                  }}
                >
                  <IconoTipo tipo={doc.doc_type} style={{ width: 16, height: 16, color: 'var(--text-secondary)', flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        className="list-badge"
                        style={{ cursor: 'pointer', border: 0 }}
                        onClick={() => openDoc(doc)}
                        title={esFichero ? 'Descargar el fichero' : doc.doc_type === 'url' ? 'Abrir el enlace' : 'Abrir el editor'}
                      >
                        {doc.title}
                      </button>
                      <span className={`list-badge ${doc.status === 'published' ? 'list-badge--on' : 'list-badge--warn'}`}>
                        {doc.status === 'published' ? 'Publicado' : 'Borrador'}
                      </span>
                      {doc.link_count > 1 && (
                        <span className="list-hint">vinculado a {doc.link_count} elementos</span>
                      )}
                    </div>
                    {doc.description && (
                      <p className="list-hint" style={{ margin: '2px 0 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {doc.description}
                      </p>
                    )}
                    {/* Quién y cuándo: es lo que se pregunta siempre al abrir la
                        documentación de un elemento. */}
                    <div className="doc-row-meta">
                      {autor && (
                        <span title={doc.created_by}>
                          Creado por <strong>{autor}</strong>
                        </span>
                      )}
                      {creado && <span title={doc.created_at}>el {creado}</span>}
                      {seHaTocado && modificado && <span title={doc.updated_at}>· modificado el {modificado}</span>}
                      {tieneHistorial && (
                        <span>· {doc.latest_version} {doc.latest_version === 1 ? 'versión' : 'versiones'}</span>
                      )}
                    </div>
                  </div>
                  {tieneHistorial && (
                    <button
                      type="button"
                      className="list-action-btn"
                      onClick={() => openDoc(doc, { tabVersiones: true })}
                      title="Ver el historial de cambios"
                    >
                      <ClockIcon />
                    </button>
                  )}
                  <button
                    type="button"
                    className="list-action-btn"
                    onClick={() => openDoc(doc)}
                    title={esFichero ? 'Descargar' : doc.doc_type === 'url' ? 'Abrir el enlace' : 'Abrir el editor'}
                  >
                    {esFichero
                      ? <ArrowDownTrayIcon />
                      : <ArrowTopRightOnSquareIcon />}
                  </button>
                  <button
                    type="button"
                    className="list-action-btn list-action-btn--danger"
                    onClick={() => setADesvincular(doc)}
                    title="Quitar este documento del elemento (no borra el documento)"
                  >
                    <XMarkIcon />
                  </button>
                </div>
              );
            })}
          </div>

          {/* Las acciones de la ventana van aquí, pegadas a la lista y en pequeño:
              al pie quedaban cinco botones grandes que aplastaban el contenido y
              partían el texto en dos líneas. El pie se queda solo con Cerrar. */}
          {mode === 'list' && (
            <div className="doc-modal-actions">
              <button type="button" className="list-chip-btn doc-chip" onClick={() => setMode('search')}>
                <MagnifyingGlassIcon /> Vincular existente
              </button>
              <button type="button" className="list-chip-btn doc-chip" onClick={handleNewRichtext}>
                <DocumentPlusIcon /> Nuevo documento
              </button>
              <button
                type="button"
                className="list-chip-btn doc-chip"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                <PaperClipIcon /> {uploading ? 'Subiendo…' : 'Subir archivo'}
              </button>
              <button type="button" className="list-chip-btn doc-chip" onClick={() => setMode('url')}>
                <GlobeAltIcon /> Añadir enlace
              </button>
            </div>
          )}
        </Dialog>
      )}

      {/* ── Confirmación de desvincular (diálogo común, antes window.confirm) ── */}
      {aDesvincular && (
        <Dialog
          title="Quitar el documento del elemento"
          onClose={() => setADesvincular(null)}
          footer={(
            <>
              <button type="button" className="list-btn-secondary" onClick={() => setADesvincular(null)}>
                Cancelar
              </button>
              <button type="button" className="list-btn-danger" onClick={() => unlinkDoc(aDesvincular)}>
                Quitar
              </button>
            </>
          )}
        >
          <p style={{ margin: 0 }}>
            Se quita <strong>{aDesvincular.title}</strong> de este elemento. El documento sigue
            existiendo y seguirá vinculado a los demás elementos que lo tengan.
          </p>
        </Dialog>
      )}
    </>
  );
}
